import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import Table from "../../components/Table";
import AddDialog from "../../components/Modal/AddModal";
import { useAuth } from "../../context/AuthContext";
import { addStockService, getStockService } from "../../services/stockService";
import { API_ORIGIN } from "../../services/httpService";
import { showErrorToast } from "../../services/toastService";
import { hasPermission } from "../../utils/permissions";

type StockItem = {
    _id: string;
    productName: string;
    cost: number;
    sellingPrice: number;
    quantity: number;
    imageUrl: string;
};

const asRecord = (value: unknown): Record<string, unknown> =>
    value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const asString = (value: unknown): string => (typeof value === "string" ? value : "");

const asNumber = (value: unknown): number => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
    }
    return 0;
};

const readErrorMessage = (err: unknown, fallback: string): string => {
    if (!err || typeof err !== "object") return fallback;
    const errObj = err as { response?: { data?: { message?: string } } };
    return errObj.response?.data?.message ?? fallback;
};

const buildImageUrl = (url?: string): string => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return url.startsWith("/") ? `${API_ORIGIN}${url}` : `${API_ORIGIN}/${url}`;
};

const Stock = () => {
    const { user } = useAuth();
    const canEditStock = hasPermission(user, "stock.write");
    const [tableData, setTableData] = useState<StockItem[]>([]);
    const [addStock, setAddStock] = useState(false);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string>("");
    const [error, setError] = useState("");

    const fetchStock = useCallback(async () => {
        if (!user?.id) return;

        setError("");
        try {
            const response = await getStockService();
            const normalized = response.map((entry) => {
                const item = asRecord(entry);
                const imageObj = asRecord(item.image);
                const imagePath =
                    asString(imageObj.url) ||
                    asString(item.imageUrl) ||
                    asString(item.productImage) ||
                    asString(item.image);

                return {
                    _id: asString(item._id) || asString(item.id) || `${asString(item.productName)}-${Math.random()}`,
                    productName: asString(item.productName) || asString(item.name),
                    cost: asNumber(item.cost),
                    sellingPrice: asNumber(item.sellingPrice),
                    quantity: asNumber(item.quantity),
                    imageUrl: buildImageUrl(imagePath),
                };
            });
            setTableData(normalized);
        } catch (err: unknown) {
            console.error("Failed to fetch stock", err);
            setError(readErrorMessage(err, "Failed to load stock"));
        }
    }, [user?.id]);

    useEffect(() => {
        fetchStock();
    }, [fetchStock]);

    const openPopUp = () => {
        setAddStock(true);
    };

    const closePopUp = () => {
        setAddStock(false);
        setImageFile(null);
        setImagePreview("");
        formik.resetForm();
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null;
        setImageFile(file);

        if (!file) {
            setImagePreview("");
            return;
        }

        const reader = new FileReader();
        reader.onload = () => setImagePreview(typeof reader.result === "string" ? reader.result : "");
        reader.readAsDataURL(file);
    };

    const columns = useMemo(
        () => [
            {
                Header: "Image",
                accessor: "imageUrl",
                Cell: ({ value }: { value: string }) =>
                    value ? (
                        <img src={value} alt="Product" className="h-12 w-12 object-cover rounded-md border" />
                    ) : (
                        <span className="text-gray-400">-</span>
                    ),
            },
            { Header: "Product Name", accessor: "productName" },
            { Header: "Cost", accessor: "cost" },
            { Header: "Selling Price", accessor: "sellingPrice" },
            { Header: "Quantity", accessor: "quantity" },
            {
                Header: "Profit",
                Cell: ({ row }: { row: { original: StockItem } }) =>
                    (row.original.sellingPrice - row.original.cost).toFixed(2),
            },
        ],
        []
    );

    const formik = useFormik({
        initialValues: {
            productName: "",
            cost: "",
            sellingPrice: "",
            quantity: "",
        },
        validationSchema: Yup.object({
            productName: Yup.string().required("Product name is required"),
            cost: Yup.number().typeError("Cost is required").required("Cost is required").min(0),
            sellingPrice: Yup.number().typeError("Selling price is required").required("Selling price is required").min(0),
            quantity: Yup.number().typeError("Quantity is required").required("Quantity is required").min(0),
        }),
        onSubmit: async (values, { resetForm }) => {
            if (!user?.id) {
                showErrorToast("User not logged in");
                return;
            }

            try {
                const formData = new FormData();
                formData.append("productName", values.productName);
                formData.append("cost", String(values.cost));
                formData.append("sellingPrice", String(values.sellingPrice));
                formData.append("quantity", String(values.quantity));

                if (imageFile) {
                    formData.append("productImage", imageFile);
                }

                await addStockService(formData);
                await fetchStock();

                resetForm();
                setImageFile(null);
                setImagePreview("");
                setAddStock(false);
            } catch (err: unknown) {
                console.error("Failed to add stock", err);
                showErrorToast(readErrorMessage(err, "Failed to add product"));
            }
        },
    });

    return (
        <div className="p-6 w-full">
            <h1 className="text-2xl font-bold mb-4">Stock</h1>
            {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

            <Table columns={columns} data={tableData} pageSize={10} add={canEditStock} onOpen={openPopUp} />

            <AddDialog title="Add New Product" isOpen={addStock} onClose={closePopUp} maxWidth="max-w-xl">
                <form onSubmit={formik.handleSubmit} className="mb-2 space-y-4">
                    <input
                        type="text"
                        name="productName"
                        placeholder="Product Name"
                        value={formik.values.productName}
                        onChange={formik.handleChange}
                        className="w-full px-3 py-2 border rounded-lg"
                    />
                    {formik.touched.productName && formik.errors.productName && (
                        <p className="text-red-500 text-sm">{formik.errors.productName}</p>
                    )}

                    <input
                        type="number"
                        name="cost"
                        placeholder="Cost"
                        value={formik.values.cost}
                        onChange={formik.handleChange}
                        className="w-full px-3 py-2 border rounded-lg"
                    />
                    {formik.touched.cost && formik.errors.cost && (
                        <p className="text-red-500 text-sm">{formik.errors.cost}</p>
                    )}

                    <input
                        type="number"
                        name="sellingPrice"
                        placeholder="Selling Price"
                        value={formik.values.sellingPrice}
                        onChange={formik.handleChange}
                        className="w-full px-3 py-2 border rounded-lg"
                    />
                    {formik.touched.sellingPrice && formik.errors.sellingPrice && (
                        <p className="text-red-500 text-sm">{formik.errors.sellingPrice}</p>
                    )}

                    <input
                        type="number"
                        name="quantity"
                        placeholder="Quantity"
                        value={formik.values.quantity}
                        onChange={formik.handleChange}
                        className="w-full px-3 py-2 border rounded-lg"
                    />
                    {formik.touched.quantity && formik.errors.quantity && (
                        <p className="text-red-500 text-sm">{formik.errors.quantity}</p>
                    )}

                    <div>
                        <label className="block text-sm font-medium mb-1">Product Image</label>
                        <input type="file" accept="image/*" onChange={handleFileChange} className="w-full" />
                    </div>

                    {imagePreview && (
                        <div className="pt-1">
                            <img src={imagePreview} alt="Preview" className="h-24 w-24 object-cover rounded-md border" />
                        </div>
                    )}

                    <button type="submit" className="px-4 py-2 bg-[#ac120c] text-white rounded-lg hover:bg-[#8f100b]">
                        Add Product
                    </button>
                </form>
            </AddDialog>
        </div>
    );
};

export default Stock;
